import React from 'react';
import { parseTagsFromContent } from '../../utils/cabTagSerializer';

/**
 * Read-only renderer for message content containing `{{ticket|epic:...}}` (and CAB
 * `{{tag:...}}`) tokens. Renders each reference as a chip and the rest as text, using
 * the shared cabTagSerializer parser. Shared by the transcript (ChatMessage) and the
 * queued-message card (QueuedMessageBanner) so chips look and behave identically.
 *
 * - Pass `onTicketClick` / `onEpicClick` to make ticket / epic chips clickable;
 *   omit them for a purely read-only render.
 * - Pass `maxChars` to collapse to a character budget without ever cutting a chip
 *   in half (chips are always rendered whole; only trailing text gets an ellipsis).
 */
const CHIP_BASE_CLASS =
  'inline-flex items-center gap-1 bg-bg-secondary border border-text-primary rounded px-1.5 py-0.5 mx-0.5 my-0.5 text-xs font-medium text-text-primary font-mono align-middle';

interface TaggedContentProps {
  content: string;
  onTicketClick?: (ticketId: string) => void;
  onEpicClick?: (epicId: string) => void;
  /** When set, collapse the render to this many characters (chip-safe). */
  maxChars?: number;
}

export const TaggedContent: React.FC<TaggedContentProps> = ({
  content,
  onTicketClick,
  onEpicClick,
  maxChars,
}) => {
  const { segments } = parseTagsFromContent(content);
  const collapse = typeof maxChars === 'number';
  const nodes: React.ReactNode[] = [];
  let used = 0;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];

    if (seg.type === 'tag' && seg.tag) {
      const tag = seg.tag;
      const isClickableTicket = tag.kind === 'ticket' && !!tag.ticketId && !!onTicketClick;
      const isClickableEpic = tag.kind === 'epic' && !!tag.epicId && !!onEpicClick;

      if (isClickableTicket || isClickableEpic) {
        const isEpic = isClickableEpic;
        const refId = isEpic ? tag.epicId! : tag.ticketId!;
        const activate = () => (isEpic ? onEpicClick!(refId) : onTicketClick!(refId));
        nodes.push(
          <span
            key={`tag-${i}`}
            role="button"
            tabIndex={0}
            title={isEpic ? 'Open epic' : `Open ${refId}`}
            onClick={activate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            }}
            className={`${CHIP_BASE_CLASS} cursor-pointer hover:underline transition-opacity hover:opacity-80`}
            style={{ verticalAlign: 'middle' }}
          >
            {tag.label}
          </span>
        );
      } else {
        nodes.push(
          <span key={`tag-${i}`} className={CHIP_BASE_CLASS} style={{ verticalAlign: 'middle' }}>
            {tag.label}
          </span>
        );
      }
      used += tag.label.length + 1;
    } else {
      const text = seg.content;
      if (collapse && used + text.length > maxChars!) {
        nodes.push(
          <React.Fragment key={`text-${i}`}>{text.slice(0, Math.max(0, maxChars! - used))}…</React.Fragment>
        );
        break;
      }
      nodes.push(<React.Fragment key={`text-${i}`}>{text}</React.Fragment>);
      used += text.length;
    }
  }

  return <>{nodes}</>;
};

export default TaggedContent;
